/**
 * A REGRA da folha de pagamento — INSS, IRRF e o holerite. Módulo PURO.
 *
 * Zero import, zero rede, zero relógio, e — a parte que mais importa — **zero
 * valor de negócio escrito aqui**. Todo número que a lei publica entra por
 * `ParametrosFolha`, que é o retorno de `dp_parametros_folha()` (000108). É a
 * mesma disciplina de `dasMei.ts`, e pela mesma razão: em janeiro muda o banco,
 * não este arquivo.
 *
 * A conta NÃO é reescrita em plpgsql. Duas definições do mesmo imposto é o
 * defeito de "agregar acesso em JavaScript" com outra roupa — o banco entrega os
 * insumos, este módulo calcula, e a prova (`app/tests/folha-dp.mjs`) confere com
 * números à mão.
 *
 * ---------------------------------------------------------------------------
 * O que ficou AQUI e não no banco, e por quê
 * ---------------------------------------------------------------------------
 * Os percentuais abaixo são ESTRUTURAIS: estão na Constituição (o 1/3 de férias,
 * o adicional de 50% da hora extra) ou na CLT (20% do noturno, 30% da
 * periculosidade, 10/20/40 da insalubridade, 8% do FGTS, 6% do vale-transporte).
 * Mudariam só com emenda ou reforma — e aí é deploy de todo modo.
 *
 * Dar campo editável a eles seria "portão largo sem leitor é permissão sem
 * dono": campo que ninguém vai editar em janeiro é campo que só serve para
 * alguém errar. O que expira — salário mínimo, faixas, deduções — está no banco.
 */

// ---------------------------------------------------------------------------
// Os parâmetros que vêm do banco
// ---------------------------------------------------------------------------

/** Uma faixa de tabela progressiva. `limite: null` é a faixa sem teto. */
export interface FaixaProgressiva {
  limite: number | null;
  aliquota: number;
  parcelaDeduzir: number;
}

/** A redução mensal do IRPF da reforma de 2026 (isenção efetiva até R$ 5.000). */
export interface ReducaoIr {
  limiteIntegral: number;
  valorIntegral: number;
  limiteFinal: number;
  base: number;
  coeficiente: number;
}

/**
 * O retorno de `dp_parametros_folha()`, já convertido para número.
 *
 * A conversão acontece na FRONTEIRA com o servidor (`hubDp.ts`), nunca aqui: o
 * PostgREST devolve `numeric` como string, e `"1621.00" + 1` é `"1621.001"` —
 * o holerite sairia com um valor plausível e errado. É a mesma cicatriz que o
 * DAS do MEI pagou.
 */
export interface ParametrosFolha {
  salarioMinimo: number;
  deducaoDependente: number;
  descontoSimplificado: number;
  reducaoIr: ReducaoIr;
  /** Limite da última faixa do INSS. DERIVADO no banco — não recalcule aqui. */
  inssTeto: number;
  /** O que a última faixa cobra no teto. DERIVADO no banco. */
  inssDescontoMaximo: number;
  faixas: { inss: FaixaProgressiva[]; irrf: FaixaProgressiva[] };
  /** A que legislação cada tabela se refere. Vai para a tela, ao lado do número. */
  referenciaInss: string;
  referenciaIrrf: string;
}

// ---------------------------------------------------------------------------
// Os percentuais estruturais (ver o cabeçalho)
// ---------------------------------------------------------------------------

/**
 * Jornada mensal padrão para achar o valor da hora: 220 horas (44 semanais).
 * Contrato com jornada menor tem divisor menor — a calculadora usa este como
 * padrão porque é o caso da maioria, e a tela diz qual está usando.
 */
export const HORAS_MES = 220;

/** Adicional mínimo da hora extra: 50% (CF art. 7º, XVI). */
export const ADICIONAL_HE = 0.5;

/** Adicional noturno urbano: 20% sobre a hora normal (CLT art. 73). */
export const ADICIONAL_NOTURNO = 0.2;

/** Periculosidade: 30% sobre o salário BASE (CLT art. 193, §1º). */
export const ADICIONAL_PERICULOSIDADE = 0.3;

/**
 * Insalubridade: 10/20/40% sobre o SALÁRIO MÍNIMO (CLT art. 192).
 *
 * A base é o mínimo, e não o salário do empregado, porque é o que a CLT diz e o
 * que a prática de DP faz. A Súmula Vinculante 4 do STF declarou a base
 * inconstitucional mas manteve sua aplicação até lei ou convenção dispor de
 * outro modo — então o mínimo segue sendo o padrão. CCT que fixe outra base é
 * caso a caso, e a tela avisa disso.
 */
export const INSALUBRIDADE = { nenhuma: 0, minimo: 0.1, medio: 0.2, maximo: 0.4 } as const;
export type GrauInsalubridade = keyof typeof INSALUBRIDADE;

/** FGTS: 8% de depósito sobre a remuneração (Lei 8.036/90 art. 15). */
export const ALIQUOTA_FGTS = 0.08;

/** Vale-transporte: desconto de até 6% do salário base (Lei 7.418/85 art. 4º). */
export const LIMITE_DESCONTO_VT = 0.06;

/** Dias do mês para salário proporcional e desconto de falta (CLT art. 64). */
export const DIAS_MES = 30;

// ---------------------------------------------------------------------------
// Aritmética de dinheiro
// ---------------------------------------------------------------------------

/**
 * Arredonda para centavos, half-up em magnitude. Existe como função nomeada — e
 * não `+x.toFixed(2)` espalhado — porque toda soma de reais deste módulo passa
 * por aqui, e é o único lugar onde a decisão de arredondamento pode ser lida.
 *
 * ---------------------------------------------------------------------------
 * Por que o `toFixed(10)` no meio, que parece supérfluo e não é
 * ---------------------------------------------------------------------------
 * A primeira versão era `Math.round((v + Number.EPSILON) * 100) / 100`, e ela
 * errava um centavo no INSS de quem ganha exatamente um salário mínimo. Medido:
 *
 *   1621 * 0.075            = 121.57499999999999   (não 121.575)
 *   × 100                   = 12157.499999999998
 *   Math.round              = 12157                → R$ 121,57
 *
 * O certo é R$ 121,58. Somar EPSILON a `v` não conserta porque o resíduo nasce
 * DEPOIS, na multiplicação — e EPSILON (2,2e-16) é pequeno demais na escala de
 * 121,575 para mudar o produto. `toFixed(10)` normaliza o resíduo binário antes
 * do arredondamento; dez casas porque folha de pagamento não tem precisão além
 * disso, e o resíduo mora na 12ª.
 *
 * `Math.abs` + `Math.sign` em vez de `Math.round` direto porque `Math.round`
 * arredonda para cima também no negativo (`-0,5` → `-0`), e a folha arredonda
 * half-up em MAGNITUDE. Quem pegou isto foi o caso 2 da prova, na fronteira
 * exata da primeira faixa do INSS.
 */
export const centavos = (v: number): number => {
  const escalado = Number((v * 100).toFixed(10));
  return (Math.sign(escalado) * Math.round(Math.abs(escalado))) / 100;
};

/**
 * A faixa que cobre a base numa tabela progressiva.
 *
 * A busca é pela PRIMEIRA cujo limite alcança a base, e a faixa sem teto
 * (`limite: null`) cobre qualquer valor. O banco garante que ela é a última e
 * que existe no máximo uma (`dp_faixas_coerentes`) — se estivesse no meio, tudo
 * depois dela ficaria inalcançável e a tabela calcularia errado sem erro nenhum.
 *
 * Base exatamente no limite cai NA faixa (`<=`), que é como a tabela é
 * publicada: "até R$ 1.621,00 — 7,5%".
 */
export function faixaDe(faixas: FaixaProgressiva[], base: number): FaixaProgressiva | null {
  for (const f of faixas) {
    if (f.limite === null || base <= f.limite) return f;
  }
  return null;
}

// ---------------------------------------------------------------------------
// INSS
// ---------------------------------------------------------------------------

export interface ResultadoInss {
  valor: number;
  /** A alíquota da faixa em que a base caiu (nominal, não a efetiva). */
  aliquotaFaixa: number;
  /** A base efetivamente usada — igual ao teto quando a remuneração o excede. */
  baseAplicada: number;
  /** True quando a remuneração passou do teto: a tela precisa dizer isso. */
  noTeto: boolean;
  /** valor ÷ remuneração. É o número que o aluno de DP costuma querer ver. */
  aliquotaEfetiva: number;
}

/**
 * Contribuição do EMPREGADO ao INSS, por faixas com parcela a deduzir.
 *
 * A tabela é progressiva por faixas: a forma "alíquota da faixa × base − parcela
 * a deduzir" dá o mesmo resultado que somar fatia por fatia, e é como a Receita
 * publica. Acima do teto, a contribuição para de crescer — por isso a base é
 * truncada ANTES de escolher a faixa, senão quem ganha acima do teto não cairia
 * em faixa nenhuma.
 */
export function calcularInss(remuneracao: number, p: ParametrosFolha): ResultadoInss {
  if (!(remuneracao > 0)) {
    return { valor: 0, aliquotaFaixa: 0, baseAplicada: 0, noTeto: false, aliquotaEfetiva: 0 };
  }
  const noTeto = remuneracao > p.inssTeto;
  const baseAplicada = noTeto ? p.inssTeto : remuneracao;
  const faixa = faixaDe(p.faixas.inss, baseAplicada);
  if (!faixa) {
    // Tabela sem faixa que cubra a base só acontece com catálogo corrompido, e
    // devolver zero em silêncio seria um holerite sem INSS. Estourar é o certo:
    // quem vê é a tela, não o aluno com um número errado na mão.
    throw new Error("a tabela do INSS não tem faixa que cubra a base — confira os parâmetros");
  }
  const valor = Math.max(0, centavos(baseAplicada * faixa.aliquota - faixa.parcelaDeduzir));
  return {
    valor,
    aliquotaFaixa: faixa.aliquota,
    baseAplicada,
    noTeto,
    aliquotaEfetiva: remuneracao > 0 ? valor / remuneracao : 0,
  };
}

// ---------------------------------------------------------------------------
// IRRF
// ---------------------------------------------------------------------------

export type ModeloIrrf = "completo" | "simplificado";

export interface ResultadoIrrf {
  modelo: ModeloIrrf;
  base: number;
  aliquotaFaixa: number;
  /** O imposto que a tabela cobra, ANTES da redução de 2026. */
  impostoTabela: number;
  /** A redução aplicada (0 quando a remuneração passa do limite final). */
  reducao: number;
  /** O imposto final: tabela − redução, com piso zero. */
  valor: number;
}

/**
 * A redução mensal do IRPF da reforma de 2026.
 *
 * Incide sobre os RENDIMENTOS TRIBUTÁVEIS do mês (a remuneração bruta), não
 * sobre a base pós-INSS — e é isso que faz dois empregados com a mesma base
 * terem reduções diferentes. Duas faixas: valor fixo até o primeiro limite,
 * depois uma reta decrescente que zera no segundo.
 *
 * Os cinco parâmetros andam juntos, e a `000108` prova no banco que a reta
 * encosta no valor fixo na fronteira — sem isso o imposto saltaria de um centavo
 * de renda para o outro.
 */
export function reducaoIr(rendimentosTributaveis: number, p: ParametrosFolha): number {
  const r = p.reducaoIr;
  if (rendimentosTributaveis <= r.limiteIntegral) return r.valorIntegral;
  if (rendimentosTributaveis <= r.limiteFinal) {
    return Math.max(0, centavos(r.base - r.coeficiente * rendimentosTributaveis));
  }
  return 0;
}

/**
 * IRRF mensal, nos dois modelos de dedução.
 *
 * **completo** (Modelo 1): base = rendimento − INSS − dependentes × dedução.
 *
 * **simplificado** (Modelo 2): base = rendimento − desconto simplificado, e o
 * INSS **não** é abatido. Não é esquecimento: o desconto simplificado SUBSTITUI
 * todas as deduções legais, o INSS entre elas (Lei 13.149/2015 e a redação
 * atual do art. 4º da Lei 9.250/95). Abater os dois seria deduzir a mesma coisa
 * duas vezes.
 *
 * `aplicarReducao` existe porque a origem aplica a redução de 2026 no holerite e
 * NÃO em férias nem no 13º. Ver o comentário em `feriasDecimoDp.ts`: o
 * comportamento foi preservado e a divergência está nomeada como pendência —
 * mudar regra de retenção sem Victor confirmar alteraria o imposto de gente
 * real, nas duas direções.
 */
export function calcularIrrf(
  rendimento: number,
  inss: number,
  dependentes: number,
  modelo: ModeloIrrf,
  p: ParametrosFolha,
  aplicarReducao = true,
): ResultadoIrrf {
  const base =
    modelo === "completo"
      ? Math.max(0, centavos(rendimento - inss - Math.max(0, dependentes) * p.deducaoDependente))
      : Math.max(0, centavos(rendimento - p.descontoSimplificado));

  const faixa = faixaDe(p.faixas.irrf, base);
  if (!faixa) {
    throw new Error("a tabela do IRRF não tem faixa que cubra a base — confira os parâmetros");
  }
  const impostoTabela = Math.max(0, centavos(base * faixa.aliquota - faixa.parcelaDeduzir));
  const reducao = aplicarReducao ? reducaoIr(rendimento, p) : 0;
  return {
    modelo,
    base,
    aliquotaFaixa: faixa.aliquota,
    impostoTabela,
    reducao,
    valor: Math.max(0, centavos(impostoTabela - reducao)),
  };
}

/**
 * Os dois modelos calculados, e qual sai mais barato para o empregado.
 *
 * O empate vai para o **completo** de propósito: é o modelo com deduções
 * comprováveis, e na declaração anual o contribuinte que já usa o completo não
 * troca de trilha por nada.
 */
export function compararModelosIrrf(
  rendimento: number,
  inss: number,
  dependentes: number,
  p: ParametrosFolha,
  aplicarReducao = true,
): { completo: ResultadoIrrf; simplificado: ResultadoIrrf; vantajoso: ModeloIrrf } {
  const completo = calcularIrrf(rendimento, inss, dependentes, "completo", p, aplicarReducao);
  const simplificado = calcularIrrf(rendimento, inss, dependentes, "simplificado", p, aplicarReducao);
  return {
    completo,
    simplificado,
    vantajoso: completo.valor <= simplificado.valor ? "completo" : "simplificado",
  };
}

// ---------------------------------------------------------------------------
// Holerite
// ---------------------------------------------------------------------------

export interface EntradaHolerite {
  salarioBase: number;
  /** Horas extras a 50%. */
  horasExtras: number;
  /** Horas trabalhadas em período noturno (22h–5h). */
  horasNoturnas: number;
  periculosidade: boolean;
  insalubridade: GrauInsalubridade;
  /** Faltas injustificadas, em dias. */
  faltas: number;
  valeTransporte: boolean;
  dependentes: number;
  modeloIrrf: ModeloIrrf;
  /** Divisor da hora. Padrão 220 (44h semanais). */
  horasMes?: number;
}

export interface ResultadoHolerite {
  proventos: {
    salarioBase: number;
    horaExtra: number;
    adicionalNoturno: number;
    periculosidade: number;
    insalubridade: number;
    descontoFaltas: number;
    total: number;
  };
  descontos: { inss: number; irrf: number; valeTransporte: number; total: number };
  inss: ResultadoInss;
  irrf: { completo: ResultadoIrrf; simplificado: ResultadoIrrf; vantajoso: ModeloIrrf; aplicado: ResultadoIrrf };
  /** Depósito do empregador. NÃO é desconto do empregado. */
  fgts: number;
  liquido: number;
  valorHora: number;
}

/**
 * O holerite do mês.
 *
 * Ordem que importa: os proventos somam primeiro (com as faltas já descontadas,
 * porque falta reduz a remuneração e portanto a base de tudo), daí sai o INSS,
 * e só então o IRRF — que precisa do INSS para o modelo completo.
 *
 * **Uma limitação herdada da origem, e nomeada:** o desconto de falta é
 * `salário ÷ 30 × dias`, sem o reflexo no DSR. Falta injustificada também faz
 * perder o descanso semanal remunerado da semana (Lei 605/49 art. 6º), então o
 * desconto real é maior. Não mudei porque mudar altera o líquido de quem usa a
 * ferramenta, e é decisão de Victor — está no runbook como pendência.
 */
export function calcularHolerite(e: EntradaHolerite, p: ParametrosFolha): ResultadoHolerite {
  const horasMes = e.horasMes && e.horasMes > 0 ? e.horasMes : HORAS_MES;
  const salarioBase = Math.max(0, e.salarioBase);
  const valorHora = salarioBase / horasMes;

  const horaExtra = centavos(valorHora * (1 + ADICIONAL_HE) * Math.max(0, e.horasExtras));
  const adicionalNoturno = centavos(valorHora * ADICIONAL_NOTURNO * Math.max(0, e.horasNoturnas));
  const periculosidade = e.periculosidade ? centavos(salarioBase * ADICIONAL_PERICULOSIDADE) : 0;
  // A base da insalubridade é o salário MÍNIMO, não o salário do empregado —
  // ver a constante INSALUBRIDADE para o porquê e a Súmula Vinculante 4.
  const insalubridade = centavos(p.salarioMinimo * INSALUBRIDADE[e.insalubridade]);
  const descontoFaltas = centavos((salarioBase / DIAS_MES) * Math.max(0, e.faltas));

  const total = Math.max(
    0,
    centavos(salarioBase + horaExtra + adicionalNoturno + periculosidade + insalubridade - descontoFaltas),
  );

  const inss = calcularInss(total, p);
  const comparacao = compararModelosIrrf(total, inss.valor, e.dependentes, p);
  const aplicado = e.modeloIrrf === "completo" ? comparacao.completo : comparacao.simplificado;

  // O desconto do VT é 6% do salário BASE, não da remuneração: a lei fala do
  // salário, e a hora extra do mês não aumenta o que se desconta de transporte.
  const valeTransporte = e.valeTransporte ? centavos(salarioBase * LIMITE_DESCONTO_VT) : 0;

  const totalDescontos = centavos(inss.valor + aplicado.valor + valeTransporte);

  return {
    proventos: {
      salarioBase,
      horaExtra,
      adicionalNoturno,
      periculosidade,
      insalubridade,
      descontoFaltas,
      total,
    },
    descontos: { inss: inss.valor, irrf: aplicado.valor, valeTransporte, total: totalDescontos },
    inss,
    irrf: { ...comparacao, aplicado },
    fgts: centavos(total * ALIQUOTA_FGTS),
    liquido: centavos(total - totalDescontos),
    valorHora: centavos(valorHora),
  };
}

// ---------------------------------------------------------------------------
// Apresentação
// ---------------------------------------------------------------------------

/**
 * Moeda em pt-BR.
 *
 * Cuidado ao comparar a saída disto em teste: o `toLocaleString` pt-BR separa
 * "R$" do número com espaço INFLEXÍVEL (` `). O console imprime igual ao
 * espaço comum, e a asserção vê uma diferença invisível — cicatriz medida na
 * prova do livro-caixa. As provas normalizam antes de comparar.
 */
export const reais = (v: number): string =>
  v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

/** Percentual com uma casa: 0.075 → "7,5%". */
export const percentual = (fracao: number, casas = 1): string =>
  `${(fracao * 100).toLocaleString("pt-BR", { minimumFractionDigits: casas, maximumFractionDigits: casas })}%`;
